import { Ability, AbilityBuilder } from '@casl/ability';
import type { WidgetDefinition, WidgetPermissions } from './types';

type Actions = 'view' | 'create' | 'delete';
type Subjects = 'Widget' | 'Dashboard' | 'all';
export type DashboardAbility = Ability<[Actions, Subjects]>;

export function createDashboardAbility(userRoles: string[]): DashboardAbility {
  const { can, build } = new AbilityBuilder<DashboardAbility>(Ability);

  if (userRoles.length > 0) {
    can('view', 'Dashboard');
    can('view', 'Widget');
  }

  if (userRoles.includes('admin')) {
    can('create', 'Widget');
    can('delete', 'Widget');
  }

  return build();
}

/**
 * @deprecated Solo como respaldo de `resolveWidgetPermissions` para hosts cuyo backend no evalua
 * acceso por instancia. **No llamarla directamente para decidir si un widget se ve**: cruza roles
 * con `Array.includes`, o sea un OR, y por tanto puede *ensanchar* lo que el servidor ya restringio
 * por ACL. Se conserva exportada porque es API publica del kit desde el primer release.
 */
export function canUserViewWidget(userRoles: string[], widgetRoles: string[]): boolean {
  if (widgetRoles.length === 0) return true;
  return userRoles.some(role => widgetRoles.includes(role));
}

/** Lo que el widget del catalogo necesita traer para resolver sus verbos. */
type WidgetAccessShape = Pick<WidgetDefinition, 'permissions' | 'roles_permitidos' | 'propietario_id'>;

/** El contexto del usuario, que **solo** se usa cuando el servidor no dijo nada. */
export interface WidgetPermissionsFallback {
  userRoles: string[];
  /** Si el usuario administra el catalogo. En el kit venia de `userRoles.includes('admin')`. */
  isAdmin: boolean;
}

/**
 * Los cuatro verbos del usuario sobre un widget, **con el servidor primero**.
 *
 * ## Por que existe
 *
 * El kit tenia dos criterios de acceso corriendo a la vez: el backend filtraba el catalogo por su
 * regla de instancia (ACL ∩ RBAC) y el kit lo volvia a filtrar por su cuenta con
 * `canUserViewWidget(userRoles, roles_permitidos)`. Ese segundo filtro es un OR de roles, asi que
 * podia hacer dos cosas, las dos malas:
 *
 * - **Esconder** un widget que el servidor si concedio — el caso de compartir una instancia con una
 *   persona que no tiene el modulo del widget: el backend lo manda en el catalogo, el kit lo tira
 *   por no cruzar roles, y la persona con quien se compartio no lo ve nunca. Sin error ni log.
 * - **Mostrar** acciones que el servidor va a rechazar, porque `isAdmin` era global: el boton de
 *   borrar aparecia para todo widget con dueño, sin importar la ACL de esa fila.
 *
 * ## La regla
 *
 * Si el widget trae `permissions`, se usan **tal cual y sin mezclar**: son la decision del servidor,
 * que ya compuso capacidad (RBAC) ∩ grant de instancia (ACL). Ni se amplian con `isAdmin` ni se
 * recortan con los roles — cualquiera de las dos cosas reintroduce el segundo criterio.
 *
 * Si no los trae, se cae al comportamiento historico. Eso es lo que mantiene funcionando a un host
 * con backend viejo, y es deliberado que el respaldo sea *identico* a lo de antes y no algo mas
 * seguro: cerrar por defecto aqui vaciaria el catalogo de esos hosts en una subida de version de
 * parche.
 */
export function resolveWidgetPermissions(
  widget: WidgetAccessShape,
  fallback: WidgetPermissionsFallback,
): WidgetPermissions {
  if (widget.permissions) return widget.permissions;

  // Un widget sin dueño es sembrado: el catalogo del tenant lo comparte todo el mundo y borrarlo
  // no tiene papelera. Es la misma condicion que el panel evaluaba como `!isSeed && isAdmin`.
  const esSembrado = !widget.propietario_id;

  return {
    can_view: canUserViewWidget(fallback.userRoles, widget.roles_permitidos ?? []),
    can_edit: fallback.isAdmin,
    can_delete: fallback.isAdmin && !esSembrado,
    // Compartir por instancia no existia antes de que el servidor lo resolviera: sin `permissions`
    // no hay a quien preguntar, y afirmarlo pintaria un dialogo contra un API que el host no tiene.
    can_share: false,
    // La exposicion la movia el administrador -y solo el: el backend forzaba privado a los demas al
    // crear-, asi que el respaldo es `isAdmin` a secas.
    can_publish: fallback.isAdmin,
  };
}
